import React from 'react';
import { Dropdown } from 'primereact/dropdown';

// ─── Module-level cache — one fetch shared across ALL instances ───────────────
export type InsurerOption = { name: string; id: string; naic: string };
let _cachedOptions: InsurerOption[] | null = null;
let _fetchPromise: Promise<InsurerOption[]> | null = null;

async function _apiFetch(search: string, top: number = 20): Promise<InsurerOption[]> {
  const ncToken = localStorage.getItem('NCToken') || '';
  const authToken = localStorage.getItem('igtoken') || '';
  const params = new URLSearchParams({ top: String(top), skip: '0' });
  if (search) params.set('search', search);
  const url = `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insurers?${params.toString()}`;
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${authToken}`,
      NCToken: ncToken,
    },
  });
  if (!res.ok) return [];
  const data = await res.json();
  const items: any[] = data.value ?? data ?? [];
  return items
    .map((item: any) => ({
      name: (item.companyName ?? item.name ?? '') as string,
      id:   (item.databaseId ?? '') as string,
      naic: (item.naic ?? '') as string,
    }))
    .filter(o => o.name && o.id);
}

function loadDefaults(): Promise<InsurerOption[]> {
  if (_cachedOptions) return Promise.resolve(_cachedOptions);
  if (_fetchPromise) return _fetchPromise;
  _fetchPromise = _apiFetch('').then(opts => {
    _cachedOptions = opts;
    return opts;
  });
  return _fetchPromise;
}

/**
 * NowCerts appends the carrier customerId in parentheses to carrier names
 * (e.g. "21st Century Insurance (2356)"). Strip that suffix so lookups work
 * against the plain names returned by the insurer dropdown API.
 */
function stripCarrierSuffix(name: string): string {
  return name.replace(/\s*\(\w+\)\s*$/, '').trim();
}

/**
 * Name → option, for callers that only have a carrier name (coverage data from
 * NowCerts carries insurer names, not ids). Carrier names are not unique in AMS,
 * so when several records share the name, prefer one that has an NAIC on file —
 * that is the record that can print an NAIC # on the certificate.
 */
function findInOptions(opts: InsurerOption[], name: string): InsurerOption | undefined {
  let matches = opts.filter(o => o.name === name);
  if (matches.length === 0) {
    const stripped = stripCarrierSuffix(name);
    if (stripped !== name) matches = opts.filter(o => o.name === stripped);
  }
  if (matches.length === 0) return undefined;
  return matches.find(o => o.naic) ?? matches[0];
}

/**
 * Synchronous lookup from the already-loaded cache.
 * Returns null if the cache hasn't loaded yet or name isn't found.
 * Use this inside getFormData() to avoid async timing issues.
 */
export function lookupCarrierIdSync(name: string): string | null {
  if (!name || !_cachedOptions) return null;
  return findInOptions(_cachedOptions, name)?.id ?? null;
}

export async function resolveCarrierIdByName(name: string): Promise<string | null> {
  if (!name) return null;
  const opts = await loadDefaults();
  const cached = findInOptions(opts, name);
  if (cached) return cached.id;
  // Not in the initial 20 — search the API using the stripped name
  const searchName = stripCarrierSuffix(name);
  const results = await _apiFetch(searchName);
  return findInOptions(results, name)?.id ?? null;
}

// Full carrier list, fetched once on demand for id→name resolution (a stored
// naiCid can point to any carrier in the directory, not just the first 20).
let _fullOptionsPromise: Promise<InsurerOption[]> | null = null;
function loadAllOptions(): Promise<InsurerOption[]> {
  if (!_fullOptionsPromise) _fullOptionsPromise = _apiFetch('', 1000);
  return _fullOptionsPromise;
}

export async function resolveCarrierById(id: string): Promise<InsurerOption | null> {
  if (!id) return null;
  const cachedHit = _cachedOptions?.find(o => o.id === id);
  if (cachedHit) return cachedHit;
  const all = await loadAllOptions();
  return all.find(o => o.id === id) ?? null;
}

/** Resolves a carrier databaseId (e.g. a certificate's stored naiCid) back to its name. */
export async function resolveCarrierNameById(id: string): Promise<string | null> {
  return (await resolveCarrierById(id))?.name ?? null;
}
// ─────────────────────────────────────────────────────────────────────────────

interface InsurerSearchDropdownProps {
  /** Carrier name (also the free-typed text when no option is selected). */
  value: string;
  /**
   * Carrier databaseId of the current selection. Pass this when the form tracks one:
   * carrier names are not unique, so the id is what identifies the selected row.
   */
  valueId?: string | null;
  onChange: (name: string) => void;
  /** Called with the carrier databaseId when an option is selected from the list, or null when text is typed directly. */
  onIdChange?: (id: string | null) => void;
  /** Called with the whole option when one is picked from the list (Tracker #257: the NAIC field beside the carrier follows it). */
  onPick?: (option: InsurerOption) => void;
  className?: string;
}

const DEBOUNCE_MS = 300;

/** Merge two option arrays, deduplicating by id. */
function mergeOptions(base: InsurerOption[], incoming: InsurerOption[]): InsurerOption[] {
  const seenIds = new Set(base.map(o => o.id));
  return [
    ...base,
    ...incoming.filter(o => {
      if (seenIds.has(o.id)) return false;
      seenIds.add(o.id);
      return true;
    }),
  ];
}

const InsurerSearchDropdown: React.FC<InsurerSearchDropdownProps> = ({
  value,
  valueId,
  onChange,
  onIdChange,
  onPick,
  className,
}) => {
  const [options, setOptions] = React.useState<InsurerOption[]>([]);
  // Every option ever loaded (cache + API searches), by id, so a selection can be
  // resolved even when `options` is a filtered subset.
  const allKnownRef = React.useRef<Map<string, InsurerOption>>(new Map());
  const addToKnown = (items: InsurerOption[]) => {
    items.forEach(o => allKnownRef.current.set(o.id, o));
  };
  // Track the active filter query so async callbacks can filter by the latest value
  const activeQueryRef = React.useRef('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // The option matching `valueId`, once known — kept in the list so the Dropdown can
  // render its label even when the filtered `options` no longer contain it.
  const [selected, setSelected] = React.useState<InsurerOption | null>(null);

  // Load default 20 once on first mount (shared cache — only 1 API call total)
  React.useEffect(() => {
    loadDefaults().then(opts => { addToKnown(opts); setOptions(opts); });
  }, []);

  React.useEffect(() => {
    if (!valueId) { setSelected(null); return; }
    const known = allKnownRef.current.get(valueId);
    if (known) { setSelected(known); return; }
    let cancelled = false;
    resolveCarrierById(valueId).then(opt => {
      if (cancelled || !opt) return;
      addToKnown([opt]);
      setSelected(opt);
    });
    return () => { cancelled = true; };
  }, [valueId]);

  const handleChange = (e: { value: string }) => {
    const raw = e.value ?? '';
    const picked = allKnownRef.current.get(raw);
    if (picked) {
      onChange(picked.name);
      onIdChange?.(picked.id);
      onPick?.(picked);
    } else {
      // Free text typed into the editable input
      onChange(raw);
      onIdChange?.(null);
    }
  };

  // Reset options to the full default list every time the panel opens
  const handleShow = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    activeQueryRef.current = '';
    setOptions(_cachedOptions ?? []);
  };

  // Fires when user types in the filter search box inside the dropdown panel
  const handleFilter = (e: { filter: string }) => {
    const raw = e.filter ?? '';
    const query = raw.trim().toLowerCase();

    // Always track the latest query so async callbacks can use it
    activeQueryRef.current = query;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query) {
      // Restore full cached list
      setOptions(_cachedOptions ?? []);
      return;
    }

    // 1. Instant local filter from cache so the list reacts immediately
    const localFiltered = (_cachedOptions ?? []).filter(o =>
      o.name.toLowerCase().includes(query)
    );
    setOptions(localFiltered);

    // 2. Debounced API call — merge with cache, then re-filter by the CURRENT query
    //    so the list stays filtered even after React re-renders from setOptions
    debounceRef.current = setTimeout(async () => {
      const remote = await _apiFetch(query);
      addToKnown(remote);
      const currentQuery = activeQueryRef.current;
      // Merge remote results into the cache without replacing it
      const merged = mergeOptions(_cachedOptions ?? [], remote);
      // Always filter the final list by whatever query is active right now
      const filtered = merged.filter(o =>
        o.name.toLowerCase().includes(currentQuery)
      );
      setOptions(filtered);
    }, DEBOUNCE_MS);
  };

  const isSelectedById = !!(valueId && selected && selected.id === valueId);
  const displayOptions = isSelectedById && !options.some(o => o.id === selected!.id)
    ? [selected!, ...options]
    : options;

  return (
    <Dropdown
      // Selected by id when the form knows one; otherwise the raw name/free text.
      value={isSelectedById ? selected!.id : value}
      options={displayOptions}
      optionLabel="name"
      optionValue="id"
      itemTemplate={(o: InsurerOption) => (
        <span>
          {o.name}
          {o.naic ? <small style={{ marginLeft: 8, color: '#6b7280' }}>NAIC {o.naic}</small> : null}
        </span>
      )}
      onChange={handleChange}
      onFilter={handleFilter}
      onShow={handleShow}
      filter
      editable
      placeholder=""
      className={className}
      style={{ width: '100%' }}
      emptyMessage="No carriers found"
      emptyFilterMessage="No carriers found"
    />
  );
};

export default InsurerSearchDropdown;
